import {useEffect, useEffectEvent, useRef, useState} from 'react';
import {assert} from 'assert-ts';
import {
  type AgentSubType,
  type CollectionSubType,
  Concept,
  type EntityMainType,
} from 'types';
import {HTTP_STATUS_ABORTED, HttpError} from 'api/http/types';
import type {DataLoadStatus, GetTokens} from 'api/types';
import {searchPrize} from 'api/prize';
import {searchAgents} from 'api/searchAgents';
import {searchSeries} from 'api/searchSeries';
import type {SearchResult} from 'api/searchTypes';
import {searchWork} from 'api/searchWork';
import {useGetTokens} from 'services/auth';
import {isConcreteLinkTargetEntity} from 'services/utils';
import {useColumnConfigState} from 'schema/table/hooks/useColumnConfigState';
import type {
  SearchMainEntityType,
  SearchResultValue,
  SearchSubEntityType,
} from '../types';
import {trimSearchQuery} from '../functions';

// const hasSameItems = <T>(a: T[], b: T[]) =>
//   a.length === b.length &&
//   a.every(aItem => b.includes(aItem)) &&
//   b.every(bItem => a.includes(bItem));

export type SearchLoadStatus<T> = DataLoadStatus<T>;

export const EMPTY_RESULT: SearchResult<SearchResultValue> = {
  hits: [],
  highlights: [],
  total: 0,
};
export const DEFAULT_RESULT: SearchLoadStatus<SearchResult<SearchResultValue>> =
  {
    status: 'Loaded',
    data: EMPTY_RESULT,
  };

type SearchEntityFunc = (
  search: string,
  subtypes: SearchSubEntityType[] | undefined,
  page: number,
  size: number,
  getTokens: GetTokens,
  signal: AbortSignal,
  includeHachetteWorks?: boolean,
) => Promise<SearchResult<SearchResultValue>>;

const searchEntityMap: {[type in SearchMainEntityType]: SearchEntityFunc} = {
  [Concept.agent]: (
    search: string,
    subtypes: SearchSubEntityType[] | undefined,
    page: number,
    size: number,
    getTokens: GetTokens,
    signal: AbortSignal,
  ) =>
    searchAgents(
      search,
      (subtypes?.[0] ?? Concept.person) as AgentSubType,
      page,
      size,
      getTokens,
      signal,
    ),
  [Concept.collection]: (
    search: string,
    subtypes: SearchSubEntityType[] | undefined,
    page: number,
    size: number,
    getTokens: GetTokens,
    signal: AbortSignal,
  ) =>
    searchSeries(
      search,
      (subtypes?.[0] ?? Concept.series) as CollectionSubType,
      page,
      size,
      getTokens,
      signal,
      false,
    ),
  [Concept.work]: (
    search: string,
    _subtypes: SearchSubEntityType[] | undefined,
    page: number,
    size: number,
    getTokens: GetTokens,
    signal: AbortSignal,
    includeHachetteWorks?: boolean,
  ) => searchWork(search, page, size, getTokens, signal, includeHachetteWorks),
  [Concept.prize]: (
    search: string,
    _subtypes: SearchSubEntityType[] | undefined,
    page: number,
    size: number,
    getTokens: GetTokens,
    signal: AbortSignal,
  ) => searchPrize(search, page, size, getTokens, signal),
};

export const useEntitySearch = (
  searchQuery: string,
  entityType: SearchMainEntityType,
  subtype: SearchSubEntityType | undefined,
  includeHachetteWorks?: boolean,
): SearchLoadStatus<SearchResult<SearchResultValue>> => {
  const [result, setResult] =
    useState<SearchLoadStatus<SearchResult<SearchResultValue>>>(DEFAULT_RESULT);
  const onSetResult = useEffectEvent(setResult);
  const searchQueryRef = useRef<string | undefined>(undefined);
  const pageRef = useRef<number | undefined>(undefined);
  const sizeRef = useRef<number | undefined>(undefined);
  const entityTypeRef = useRef<EntityMainType | undefined>(entityType);
  const subtypeRef = useRef<SearchSubEntityType | undefined>(subtype);
  const abortControllerRef = useRef<AbortController | undefined>(undefined);
  const includeHachetteWorksRef = useRef<boolean | undefined>(undefined);

  const schemaName = isConcreteLinkTargetEntity(entityType)
    ? entityType
    : (subtype ?? Concept.person);

  const {paginationModel, resetPaginationModel} = useColumnConfigState(
    schemaName === Concept.work ? 'workSummary' : schemaName,
  );

  const getTokens = useGetTokens();

  // When search query or entity type/subtype is changed
  // => abort any ongoing search, search with new criteria
  useEffect(() => {
    if (
      searchQuery === searchQueryRef.current &&
      paginationModel.page === pageRef.current &&
      paginationModel.pageSize === sizeRef.current &&
      entityType === entityTypeRef.current &&
      subtype === subtypeRef.current &&
      includeHachetteWorks === includeHachetteWorksRef.current
    ) {
      return;
    }

    // Abort any ongoing call
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = undefined;
    }

    // Empty search is only allowed on prize.
    if (!searchQuery && entityType !== Concept.prize) {
      onSetResult(DEFAULT_RESULT);
      return;
    }

    onSetResult(prev => ({
      ...prev,
      status: 'Loading',
    }));

    const reset =
      entityType === Concept.prize && !searchQuery
        ? (searchQuery ?? '') !== (searchQueryRef.current ?? '')
        : (searchQuery ?? '') !== (searchQueryRef.current ?? '') ||
          entityType !== entityTypeRef.current ||
          subtype !== subtypeRef.current ||
          includeHachetteWorks !== includeHachetteWorksRef.current;

    if (reset && paginationModel.page > 0) {
      resetPaginationModel();
      pageRef.current = 0;
      sizeRef.current = 20;
    } else {
      pageRef.current = paginationModel.page;
      sizeRef.current = paginationModel.pageSize;
    }

    abortControllerRef.current = new AbortController();
    searchQueryRef.current = searchQuery;
    entityTypeRef.current = entityType;
    subtypeRef.current = subtype;
    includeHachetteWorksRef.current = includeHachetteWorks;

    const trimmedQuery = trimSearchQuery(searchQuery);
    assert(
      searchEntityMap[entityType],
      'searchEntityMap[entityType] expected',
      {entityType},
    )(
      trimmedQuery,
      subtype ? [subtype] : undefined,
      pageRef.current,
      sizeRef.current,
      getTokens,
      abortControllerRef.current.signal,
      includeHachetteWorks,
    )
      .then(data => {
        onSetResult({status: 'Loaded', data});
      })
      .catch(error => {
        if (
          error instanceof HttpError &&
          error.status === HTTP_STATUS_ABORTED
        ) {
          // Caused by abort above, leave state as is - to be updated by next call
          // console.log('useEntitySearch: search aborted');
          return;
        }

        console.error('useEntitySearch: search failed', error);
        onSetResult({
          status: 'Failed',
          error,
        });
      });
  }, [
    subtype,
    getTokens,
    searchQuery,
    entityType,
    resetPaginationModel,
    paginationModel.page,
    paginationModel.pageSize,
    includeHachetteWorks,
  ]);

  return result;
};
